export interface ClockOptions {
  raf?: (cb: (ms: number) => void) => number;
  caf?: (id: number) => void;
  maxDt?: number; // in seconds; caps dt after a hitch or a background pause. Default 0.1
}

/** The frame clock. You can pass in rAF and the timestamps, so it runs without a browser.
 *  It calls back with `(time, dt)` in seconds. `dt` is capped at `maxDt`, so a background tab
 *  that comes back doesn't get one giant step. The host pauses it when the tab is hidden. */
export class Clock {
  time = 0;
  dt = 0;
  frame = 0;
  running = false;
  // The first frame after a start has no previous frame, so its dt is 0.
  private lastMs: number | null = null;
  private id = 0;
  private readonly raf: (cb: (ms: number) => void) => number;
  private readonly caf: (id: number) => void;
  private readonly maxDt: number;

  constructor(
    private readonly onTick: (time: number, dt: number) => void,
    opts: ClockOptions = {},
  ) {
    this.raf = opts.raf ?? ((cb) => (globalThis.requestAnimationFrame ?? (() => 0))(cb));
    this.caf = opts.caf ?? ((id) => globalThis.cancelAnimationFrame?.(id));
    this.maxDt = opts.maxDt ?? 0.1;
  }

  start(): void {
    if (this.running) return;
    this.running = true;
    this.lastMs = null;
    this.schedule();
  }

  stop(): void {
    this.running = false;
    if (this.id) this.caf(this.id);
    this.id = 0;
  }

  private schedule(): void {
    this.id = this.raf((ms) => this.onFrame(ms));
  }

  private onFrame(ms: number): void {
    if (!this.running) return;
    // `time` adds up the capped `dt`, not the real elapsed time, so simulation time doesn't
    // jump when a background tab comes back. Anything that measures time since a start from
    // `time` (wing beats, wave age) has to see the same capped step, or it'd snap forward on
    // resume.
    const dt = this.lastMs === null ? 0 : Math.min(this.maxDt, (ms - this.lastMs) / 1000);
    this.lastMs = ms;
    this.time += dt;
    this.dt = dt;
    this.frame++;
    this.onTick(this.time, dt);
    if (this.running) this.schedule();
  }
}
